import React from 'react';
import { Link } from 'react-router-dom';
import type { TransferRecord } from '@/data/transferRecord';
import { formatDayRange, formatRelativeTime, sentenceCaseStatus } from '@/domain/warehouse';
import { RecordSummaryCard, type RecordFact } from '../../../components/records/RecordLayout';
import { StatusChip } from '../../../components/status/StatusChip';
import {
  eventLabel,
  formatSiteDateTime,
  formatSiteDay,
  formatSiteMoment,
  shortSiteName,
  siteZoneAbbreviation,
} from './transferRecordView';

type TransferSummaryRailProps = {
  record: TransferRecord;
};

/** The summary rail: schedule, the return leg (or the outbound transfer), the event and recent activity. */
export default function TransferSummaryRail({ record }: TransferSummaryRailProps) {
  const { transfer, totals, events, timeline } = record;
  const fromZone = transfer.fromSite?.timezone ?? null;
  const toZone = transfer.toSite?.timezone ?? null;
  const temporary = transfer.scope === 'temporary';

  // Ship times are the From site's clock and arrival times the To site's. When the two sites keep
  // different zones, each time carries its zone, so a same-day receipt never reads as before the ship.
  const mixedZones = Boolean(fromZone && toZone && fromZone !== toZone);
  const withZone = (value: string | null, iso: string | null, zone: string | null) => {
    if (!value || !mixedZones || !iso) return value;
    const abbreviation = siteZoneAbbreviation(zone, new Date(iso));
    return abbreviation ? `${value} ${abbreviation}` : value;
  };
  const schedule: RecordFact[] = [];
  const needBy = withZone(formatSiteDateTime(transfer.needByAt, toZone), transfer.needByAt, toZone);
  if (needBy) schedule.push({ label: 'Need by', value: needBy });
  const shipped = withZone(formatSiteDateTime(transfer.actualShipAt, fromZone), transfer.actualShipAt, fromZone);
  const ships = withZone(formatSiteMoment(transfer.expectedShipAt, fromZone), transfer.expectedShipAt, fromZone);
  if (shipped) schedule.push({ label: 'Shipped', value: shipped });
  else if (ships) schedule.push({ label: 'Ships', value: ships });
  const received = withZone(formatSiteDateTime(transfer.actualReceiveAt, toZone), transfer.actualReceiveAt, toZone);
  const arrives = withZone(formatSiteMoment(transfer.expectedReceiveAt, toZone), transfer.expectedReceiveAt, toZone);
  if (received) schedule.push({ label: 'Received', value: received });
  else if (arrives) schedule.push({ label: 'Arrives', value: arrives });
  const back = temporary ? formatSiteDay(transfer.expectedReturnAt, toZone) : null;
  if (back) schedule.push({ label: 'Return due', value: back });
  schedule.push({ label: 'Type', value: temporary ? 'Temporary' : 'Permanent' });

  const leg = transfer.returnLeg;
  const parent = transfer.parent;
  const legLines = leg
    ? leg.lineCount > 0
      ? `${leg.lineCount.toLocaleString()} ${leg.lineCount === 1 ? 'line' : 'lines'}`
      : `Copies the ${totals.lineCount.toLocaleString()} received ${totals.lineCount === 1 ? 'line' : 'lines'}`
    : null;
  const event = events[0] ?? null;
  const recent = timeline.slice(0, 4);

  return (
    <aside className="event-current-summary-rail transfer-record-rail" aria-label="Transfer summary">
      <RecordSummaryCard title="Schedule" facts={schedule} />

      {leg ? (
        <RecordSummaryCard
          title="Return leg"
          ariaLabel={`Return leg ${leg.docNo ?? ''}`.trim()}
          lead={(
            <span className="transfer-record-rail__lead">
              <span>{leg.docNo ?? 'Return leg'}</span>
              <StatusChip kind="transfer" status={leg.status} size="sm" />
            </span>
          )}
          facts={[
            { label: 'Route', value: `${shortSiteName(transfer.toSite?.name)} → ${shortSiteName(transfer.fromSite?.name)}` },
            { label: 'Ships', value: formatSiteDay(leg.expectedShipAt ?? transfer.expectedReturnAt, toZone) ?? 'Not set' },
            { label: 'Lines', value: legLines ?? '—' },
          ]}
          actions={<Link className="btn sm" to={`/transfers/${encodeURIComponent(leg.id)}/lines`}>Open return</Link>}
        />
      ) : null}

      {parent ? (
        <RecordSummaryCard
          title="Return of"
          ariaLabel={`Return of ${parent.docNo ?? 'the outbound transfer'}`}
          lead={(
            <span className="transfer-record-rail__lead">
              <span>{parent.docNo ?? 'Outbound transfer'}</span>
              <StatusChip kind="transfer" status={parent.status} size="sm" />
            </span>
          )}
          actions={<Link className="btn sm" to={`/transfers/${encodeURIComponent(parent.id)}/lines`}>Open outbound</Link>}
        />
      ) : null}

      {event ? (
        <RecordSummaryCard
          title="Event"
          lead={eventLabel(event)}
          facts={[
            { label: 'Date', value: formatDayRange(event.startDate, event.endDate === event.startDate ? null : event.endDate) ?? 'Not set' },
            { label: 'Site', value: event.site?.name ?? '—' },
            { label: 'Stage', value: event.status ? sentenceCaseStatus(event.status) : '—' },
          ]}
          actions={<Link className="btn sm" to={`/events/${encodeURIComponent(event.id)}`}>Open event</Link>}
        />
      ) : null}

      <RecordSummaryCard title="Recent activity">
        {recent.length ? (
          <ol className="transfer-record-activity">
            {recent.map((entry) => (
              <li key={entry.id}>
                <time dateTime={entry.at} title={formatSiteDateTime(entry.at, fromZone) ?? undefined}>
                  {formatRelativeTime(entry.at)}
                </time>
                <span>
                  {entry.summary}
                  {entry.actor?.name ? <span className="transfer-record-activity__actor"> · {entry.actor.name}</span> : null}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="transfer-record-empty">Nothing recorded yet.</p>
        )}
      </RecordSummaryCard>
    </aside>
  );
}
